import type { PlayerId, PlayerData, NetworkMessage, Vector3 } from 'shared';

export interface NetworkEvents {
  onConnect?: (playerId: PlayerId) => void;
  onDisconnect?: () => void;
  onPlayerJoin?: (player: PlayerData) => void;
  onPlayerLeave?: (playerId: PlayerId) => void;
  onPlayerMove?: (playerId: PlayerId, position: Vector3, rotation: Vector3, velocity?: Vector3) => void;
  onWorldState?: (players: PlayerData[], tick: number) => void;
  onChat?: (playerId: PlayerId, playerName: string, message: string, channel?: string) => void;
}

export class NetworkManager {
  private ws: WebSocket | null = null;
  private playerId: PlayerId | null = null;
  private serverUrl: string;
  private reconnectAttempts: number = 0;
  private maxReconnectAttempts: number = 5;
  private reconnectDelay: number = 2000;
  private isConnected: boolean = false;

  // Position update throttling
  private lastPositionUpdate: number = 0;
  private positionUpdateRate: number = 50; // 20 updates per second

  // Event handlers
  public events: NetworkEvents = {};

  constructor(serverUrl?: string) {
    // Use environment variable, passed URL, or fall back to localhost
    // @ts-ignore - Vite injects import.meta.env
    const envServerUrl = import.meta.env?.VITE_SERVER_URL;

    if (serverUrl) {
      this.serverUrl = serverUrl;
    } else if (envServerUrl) {
      this.serverUrl = envServerUrl;
    } else {
      // Auto-detect protocol based on page protocol (ws:// for http://, wss:// for https://)
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const host = window.location.hostname;

      if (host === 'localhost' || host === '127.0.0.1') {
        // Local development: use ws:// with port 8080
        this.serverUrl = `${protocol}//${host}:8080`;
      } else {
        // Production: VITE_SERVER_URL should be set, warn if not
        console.warn('⚠️ VITE_SERVER_URL not set. WebSocket connection may fail in production.');
        this.serverUrl = `${protocol}//${host}`;
      }
    }
  }

  public connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        console.log(`🌐 Connecting to ${this.serverUrl}...`);
        this.ws = new WebSocket(this.serverUrl);

        this.ws.onopen = () => {
          console.log('✅ WebSocket connected');
          this.isConnected = true;
          this.reconnectAttempts = 0;
        };

        this.ws.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data) as NetworkMessage;
            this.handleMessage(message);

            // Resolve promise on first connect message
            if (message.type === 'connect' && message.playerId) {
              this.playerId = message.playerId;
              resolve();
            }
          } catch (error) {
            console.error('Failed to parse server message:', error);
          }
        };

        this.ws.onclose = () => {
          console.log('🔌 WebSocket disconnected');
          this.isConnected = false;
          this.events.onDisconnect?.();
          this.attemptReconnect();
        };

        this.ws.onerror = (error) => {
          console.error('❌ WebSocket error:', error);
          if (!this.isConnected) {
            reject(error);
          }
        };

        // Timeout for initial connection
        setTimeout(() => {
          if (!this.isConnected) {
            reject(new Error('Connection timeout'));
          }
        }, 5000);

      } catch (error) {
        reject(error);
      }
    });
  }

  private handleMessage(message: NetworkMessage): void {
    switch (message.type) {
      case 'connect':
        if (message.playerId) {
          this.playerId = message.playerId;
          console.log(`🎮 Assigned player ID: ${this.playerId}`);
          this.events.onConnect?.(this.playerId);
        }
        break;

      case 'player_join':
        if (message.player) {
          console.log(`👤 Player joined: ${message.player.name}`);
          this.events.onPlayerJoin?.(message.player);
        }
        break;

      case 'player_leave':
        if (message.playerId) {
          console.log(`👋 Player left: ${message.playerId}`);
          this.events.onPlayerLeave?.(message.playerId);
        }
        break;

      case 'player_move':
        if (message.playerId && message.position && message.rotation) {
          this.events.onPlayerMove?.(
            message.playerId,
            message.position,
            message.rotation,
            message.velocity
          );
        }
        break;

      case 'world_state':
        if (message.players) {
          this.events.onWorldState?.(message.players, message.tick || 0);
        }
        break;

      case 'chat':
        if (message.playerId && message.playerName && message.message) {
          this.events.onChat?.(
            message.playerId,
            message.playerName,
            message.message,
            message.channel
          );
        }
        break;
    }
  }

  private attemptReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.log('❌ Max reconnection attempts reached');
      return;
    }

    this.reconnectAttempts++;
    console.log(`🔄 Reconnecting... (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);

    setTimeout(() => {
      this.connect().catch(() => {
        // Will trigger another reconnect attempt via onclose
      });
    }, this.reconnectDelay * this.reconnectAttempts);
  }

  public sendPlayerMove(position: Vector3, rotation: Vector3, velocity?: Vector3): void {
    // Throttle position updates
    const now = Date.now();
    if (now - this.lastPositionUpdate < this.positionUpdateRate) {
      return;
    }
    this.lastPositionUpdate = now;

    this.send({
      type: 'player_move',
      playerId: this.playerId || '',
      position,
      rotation,
      velocity,
      timestamp: now,
    });
  }

  public sendChat(message: string, channel: string = 'global'): void {
    this.send({
      type: 'chat',
      playerId: this.playerId || '',
      message,
      channel,
      timestamp: Date.now(),
    });
  }

  private send(message: NetworkMessage): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    }
  }

  public getPlayerId(): PlayerId | null {
    return this.playerId;
  }

  public isOnline(): boolean {
    return this.isConnected;
  }

  public disconnect(): void {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.isConnected = false;
    this.playerId = null;
  }
}
